import { Button, Callout, Link } from '@/components/Elements';
import { CheckBox, InputField } from '@/components/Form';
import { useRegister } from '@/lib/auth';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

const CONFIRMATION_EXPIRED_REASON = {
  ALREADY_CONFIRMED: 'already_confirmed',
} as const;

const createUrlWithRedirectQuery = (
  basePath: string,
  redirectPath: string | null
): string => {
  const baseUrl = new URL(basePath, window.location.origin);

  if (redirectPath) {
    baseUrl.searchParams.set('to', redirectPath);
  }

  return baseUrl.toString();
};

export const RegistrationForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const register = useRegister();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('to');
  const confirmationExpired = searchParams.get('confirmation_expired');
  const confirmationExpiredReason = searchParams.get('reason');

  const [email, setEmail] = useState<string>('');
  const [confirmTermsOfService, setConfirmTermsOfService] = useState(false);
  const [confirmPrivacyPolicy, setConfirmPrivacyPolicy] = useState(false);

  const canSubmit = confirmTermsOfService && confirmPrivacyPolicy && email;
  const showExpiredCallout = Boolean(confirmationExpired);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // 重複送信防止: 条件チェックと送信中状態の確認
    if (!canSubmit || register.isPending) return;

    register.mutate(
      {
        email,
        confirmSuccessUrl: createUrlWithRedirectQuery(
          '/setup/profile',
          redirectPath
        ),
        isTermsAgreed: confirmTermsOfService && confirmPrivacyPolicy,
      },
      {
        onSuccess: () => {
          navigate('/auth/register/complete');
        },
      }
    );
  };

  return (
    <div>
      {showExpiredCallout && (
        <Callout variant="danger" className="mb-6">
          {confirmationExpiredReason ===
          CONFIRMATION_EXPIRED_REASON.ALREADY_CONFIRMED
            ? t(
                'この確認リンクはすでに使用されています。下記から仮登録メールを再発行してください。'
              )
            : t(
                '仮登録メールの有効期限が切れています。下記から仮登録メールを再発行してください。'
              )}
        </Callout>
      )}
      <form onSubmit={onSubmit}>
        <InputField
          label={t('メールアドレス')}
          type="email"
          autoComplete="email"
          placeholder={t('メールアドレスを入力...')}
          required
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
          error={register.isError}
          errors={[
            t(
              '登録できないメールアドレスです。すでにご登録済みの方は、ログインをお試しください。'
            ),
          ]}
        />

        <div className="mt-8">
          <CheckBox
            label={
              <Trans
                i18nKey="agree-terms-and-conditions"
                components={{
                  l: (
                    <Link
                      to={t('terms-and-conditions-url')}
                      target="_blank"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('利用規約')}
                    </Link>
                  ),
                }}
              />
            }
            enabled={confirmTermsOfService}
            setEnabled={() => setConfirmTermsOfService(!confirmTermsOfService)}
          />
          <CheckBox
            label={
              <Trans
                i18nKey="agree-privacy-policy"
                components={{
                  l: (
                    <Link
                      to={t('privacy-policy-url')}
                      target="_blank"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('プライバシーポリシー')}
                    </Link>
                  ),
                }}
              />
            }
            enabled={confirmPrivacyPolicy}
            setEnabled={() => setConfirmPrivacyPolicy(!confirmPrivacyPolicy)}
          />
        </div>

        <Button
          type="submit"
          size="sm"
          className="mt-4 w-full"
          disabled={!canSubmit || register.isPending}
          isLoading={register.isPending}
        >
          {t('仮登録メールを受け取る')}
        </Button>
      </form>

      <div className="mt-2 text-sm">
        {t('すでにアカウントをお持ちですか？')}{' '}
        <Link to={createUrlWithRedirectQuery('/auth/login', redirectPath)}>
          {t('ログイン')}
        </Link>
      </div>
    </div>
  );
};
